import React from 'react';
import { Spin, Tag } from 'antd';
import { LoadingOutlined, CloseCircleOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import './styles.less';

const ProductPreviewTooltip = ({ productPreview, onClose }) => {
  const { isLoading, data, error } = productPreview;

  // Don't render if no data, no error, and not loading
  if (!isLoading && !data && !error) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="product-preview-tooltip">
        <div className="product-preview-loading">
          <Spin indicator={<LoadingOutlined spin />} size="small" />
          <span>Đang tải thông tin sản phẩm...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="product-preview-tooltip product-preview-error">
        <CloseCircleOutlined className="error-icon" />
        <span>{error}</span>
      </div>
    );
  }

  // Success state - show product info
  const {
    name,
    productId,
    images = [],
    imei,
    ram,
    lineDescription,
    state,
    battery,
    guarantee,
    amount,
    promotionPriceJPY,
    priceJPY,
    percentagePromotion,
    soldOut,
    isDeleted,
  } = data;

  const displayPrice = promotionPriceJPY || priceJPY;
  const imageUrl = images?.[0]?.url || images?.[0];
  const hasPromotion = promotionPriceJPY && priceJPY && promotionPriceJPY < priceJPY;

  // Determine product status
  const getStatusTag = () => {
    if (isDeleted) {
      return <Tag color="default">Đã xoá</Tag>;
    }
    if (soldOut) {
      return <Tag color="red">Hết hàng</Tag>;
    }
    return <Tag color="green">Còn hàng</Tag>;
  };

  return (
    <div className="product-preview-tooltip">
      <button type="button" className="close-btn" onClick={onClose} aria-label="Close">
        &times;
      </button>
      <div className="product-preview-content">
        <div className="product-image-wrapper">
          {imageUrl ? (
            <img src={imageUrl} alt={name} className="product-image" />
          ) : (
            <div className="product-image-placeholder">No Image</div>
          )}
          {percentagePromotion > 0 && (
            <span className="discount-badge">-{percentagePromotion}%</span>
          )}
        </div>
        <div className="product-info">
          <div className="product-name" title={name}>
            {name}
          </div>
          <div className="product-meta">
            {productId && <div className="meta-item">ID: {productId}</div>}
            {imei && <div className="meta-item">IMEI: {imei}</div>}
          </div>
          <div className="product-specs">
            {ram && <span className="spec-item">Bộ nhớ: {ram}</span>}
            {lineDescription && <span className="spec-item">Dòng: {lineDescription}</span>}
          </div>
          <div className="product-specs">
            {state && <span className="spec-item">Tình trạng: {state}</span>}
            {battery && <span className="spec-item">Pin: {battery}</span>}
          </div>
          {guarantee && (
            <div className="product-guarantee">Bảo hành: {guarantee} tháng</div>
          )}
          <div className="product-price">
            <span className="current-price">
              ¥{displayPrice?.toLocaleString('ja-JP')}
            </span>
            {hasPromotion && (
              <span className="original-price">
                ¥{priceJPY.toLocaleString('ja-JP')}
              </span>
            )}
          </div>
          <div className="product-bottom">
            {amount !== undefined && amount !== null && (
              <span className="stock-count">Còn: {amount} máy</span>
            )}
            <span className="product-status">{getStatusTag()}</span>
          </div>
        </div>
      </div>
      <div className="product-preview-hint">Nhấn Enter để gửi</div>
    </div>
  );
};

ProductPreviewTooltip.propTypes = {
  productPreview: PropTypes.shape({
    isLoading: PropTypes.bool,
    data: PropTypes.shape({
      name: PropTypes.string,
      productId: PropTypes.string,
      images: PropTypes.array,
      imei: PropTypes.string,
      ram: PropTypes.string,
      lineDescription: PropTypes.string,
      state: PropTypes.string,
      battery: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      guarantee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      amount: PropTypes.number,
      promotionPriceJPY: PropTypes.number,
      priceJPY: PropTypes.number,
      percentagePromotion: PropTypes.number,
      soldOut: PropTypes.bool,
      isDeleted: PropTypes.bool,
    }),
    error: PropTypes.string,
    productId: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func,
};

ProductPreviewTooltip.defaultProps = {
  onClose: () => {},
};

export default ProductPreviewTooltip;
